/**
 * 69.30 step 4 (ARCHITECTURE §5.26 (d); DECISIONS 248 (5), the owner: "sure empty + additional rules is fine"), with
 * GameConfig.munitionsSupply: a dry vehicle's bot driver takes it to a dump. Pure readers here; the errand itself is the
 * drivers' (ai/actionBot.ts techDrive, mortarDrive: `rearmRun`).
 * - **When** (69.42 (b): `rearmWanted`, weighed against the way there and the fight): its stores (core/munitions.ts
 *   rearmStores: the tank's main gun, a launcher's tubes and store, the mortar's bombs) out, or low for how far the dump is
 *   with nothing for it to fight (`busy` false: no enemy its side knows in its gun's reach, no hull in a tank's sights, no
 *   mission of a mortar's still firing).
 * - **Where:** the nearest dump holding a round it is short of — a standing crate (core/munitions.ts dumpHolds) or a parked
 *   supply truck's bed (`bedOf`). Any side's within REARM_NEAR_PEOPLE (242: open to all); past that, only its own side's
 *   (242's walk: never a long drive into the enemy's base).
 * - **How long:** parked in reach until full, or the dump has nothing more it wants, or REARM_WAIT_SECONDS pass; then back to
 *   its post (the driver's own rule picks it again).
 */
import type { AmmoType, GameState, StructureState } from '../types';
import { MUNITIONS } from '../constants';
import { bedOf, dumpHolds, rearmStores, type RearmStore } from '../core/munitions';
import { crateTeam } from '../core/supply';
import { structureTeam } from '../core/body';
import { arcDistance } from '../core/gameState';
import { driveSpec } from '../core/drive';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';

/** Under this share of its stores' cap, with nothing to fight, a vehicle goes to rearm (248 (5): "empty + additional rules"). */
export const REARM_THIRD = 1 / 3;
/** The longest it waits parked at a dump (seconds: §5.26 (d)'s minute). */
export const REARM_WAIT_SECONDS = 60;
/** Any side's dump within this (people); past it, only its own side's. */
export const REARM_NEAR_PEOPLE = 150;
/** 69.42 (b): a vehicle low but not yet due (its dump near enough, or the fight on) is weighed again after this (seconds). */
export const REARM_LOOK_SECONDS = 2;
/** After an errand ends short (no dump, the drive given up, the wait run out), none again for this long (seconds). */
export const REARM_REST_SECONDS = 30;

/** A vehicle's rounds aboard and its cap, over every store it rearms (both 0: it rearms nothing). */
export function storesLeft(v: StructureState, config: GameState['config']): { left: number; cap: number } {
  let left = 0, cap = 0;
  for (const st of rearmStores(v, config)) { cap += st.cap; left += st.cap - st.short; }
  return { left, cap };
}

/**
 * 69.42 (d) (DECISIONS 253 (2)): whether `v` is short for a fight — with the switch on, its stores together under
 * REARM_THIRD of their cap (dry, or near it: the commander counts it out of a play; it goes for its rounds). A hull with no
 * stores (a .50's belt is not one) is never short; nor anything with the switch off.
 */
export function storesShort(state: Pick<GameState, 'config'>, v: StructureState): boolean {
  if (!state.config.munitionsSupply) return false;
  const { left, cap } = storesLeft(v, state.config as GameState['config']);
  return cap > 0 && left < REARM_THIRD * cap;
}

/**
 * 69.42 (a) (DECISIONS 253 (2)): whether `v`'s rounds of `type` are low — with the switch on, under REARM_THIRD of their
 * cap (the tank's explosive or solid, a launcher's rockets or missiles, the mortar's bombs). Never with the switch off.
 */
export function roundsLow(state: Pick<GameState, 'config'>, v: StructureState, type: AmmoType): boolean {
  if (!state.config.munitionsSupply) return false;
  let left = 0, cap = 0;
  for (const st of rearmStores(v, state.config)) if (st.type === type) { cap += st.cap; left += st.cap - st.short; }
  return cap > 0 && left < REARM_THIRD * cap;
}

/**
 * 69.42 (b) (DECISIONS 253 (2): leaving for a dump in good time): when a vehicle leaves, weighed against the way there
 * (`seconds`: goSeconds, its drive to the dump) and the fight (`busy`: something for it to fight in reach). Its stores' share
 * left (over every store: rearmStores) against:
 * - **not busy:** REARM_TIMING.share (a third) for a dump at hand, rising by `perSecond` with the drive — the farther the
 *   dump, the earlier it leaves, so it does not run dry far from one — up to `most`; and a dump within `topUpSeconds` tops it
 *   up under `topUp`;
 * - **busy:** out (it has nothing to fight with), or under `busyShare` with a dump within `busySeconds` (a top-up between
 *   shots, close behind).
 * Never with the switch off.
 */
export const REARM_TIMING = { share: 1 / 3, perSecond: 1 / 300, most: 0.6, topUpSeconds: 15, topUp: 0.75, busyShare: 0.15, busySeconds: 20, speed: 0.6 };

/** Its stores' share left (1: nothing it rearms), and whether it is out. */
function shareLeft(v: StructureState, config: GameState['config']): { share: number; out: boolean } {
  const { left, cap } = storesLeft(v, config);
  return cap > 0 ? { share: left / cap, out: left <= 0 } : { share: 1, out: false };
}

/** Whether `v` might want a dump at some distance (the loosest of rearmWanted's rules): the dump's search only then. */
export function rearmMaybe(state: Pick<GameState, 'config'>, v: StructureState, busy: boolean): boolean {
  if (!state.config.munitionsSupply || v.health <= 0) return false;
  const { share, out } = shareLeft(v, state.config), T = REARM_TIMING;
  return out || share < (busy ? T.busyShare : Math.max(T.most, T.topUp));
}

/** Whether `v` wants a dump `seconds` of driving away, `busy` or not (69.42 (b)'s rule above). */
export function rearmWanted(state: Pick<GameState, 'config'>, v: StructureState, busy: boolean, seconds: number): boolean {
  if (!state.config.munitionsSupply || v.health <= 0) return false;
  const { share, out } = shareLeft(v, state.config), T = REARM_TIMING;
  if (out) return true;
  if (busy) return share < T.busyShare && seconds <= T.busySeconds;
  return share < Math.min(T.most, T.share + T.perSecond * seconds) || (seconds <= T.topUpSeconds && share < T.topUp);
}

/** The seconds `v` takes to drive to `d` (the arc at REARM_TIMING.speed of its top speed). */
export function goSeconds(state: Pick<GameState, 'config' | 'planets'>, v: StructureState, d: StructureState): number {
  const planet = state.planets[v.planetIndex];
  if (!planet) return Infinity;
  const spec = driveSpec(v.type), top = (spec.gas ?? spec.electric).top * personSetOf(state).walkSpeed;
  return arcDistance(v.surfaceDir, d.surfaceDir, planet.radius) / Math.max(1e-9, REARM_TIMING.speed * top);
}

/** The rounds dump `d` offers `stores` (a crate's or a parked supply truck's bed), or null: none it is short of. */
function offers(d: StructureState, stores: readonly RearmStore[], walkSpeed: number): Partial<Record<string, number>> | null {
  const ammo = dumpHolds(d) ? d.supply!.ammo! : bedOf(d, walkSpeed);
  return ammo && stores.some(st => st.short > 0 && (ammo[st.type] ?? 0) > 0) ? ammo : null;
}

/** Whether dump `d` still holds a round `v` is short of. */
export function dumpServes(state: Pick<GameState, 'config'>, v: StructureState, d: StructureState): boolean {
  return offers(d, rearmStores(v, state.config), personSetOf(state).walkSpeed) !== null;
}

/** How near a dump `v` must stand to take from it (world units: a crate's reach, or a truck's). */
export function dumpReach(state: Pick<GameState, 'config'>, d: StructureState): number {
  return (dumpHolds(d) ? MUNITIONS.reach : MUNITIONS.truckReach) * PERSON_BODY_HEIGHT * personSetOf(state).k;
}

/** The dump `v` of `team` drives to: the nearest holding a round it is short of — any side's within REARM_NEAR_PEOPLE, else its own side's — or null. */
export function rearmDump(state: GameState, v: StructureState, team: number): StructureState | null {
  const planet = state.planets[v.planetIndex];
  if (!planet) return null;
  const ps = personSetOf(state), near = REARM_NEAR_PEOPLE * PERSON_BODY_HEIGHT * ps.k;
  const stores = rearmStores(v, state.config);
  let best: StructureState | null = null, bestD = Infinity;
  for (const d of state.structures) {
    if (d.planetIndex !== v.planetIndex || d.id === v.id || !offers(d, stores, ps.walkSpeed)) continue;
    const at = arcDistance(v.surfaceDir, d.surfaceDir, planet.radius);
    if (at >= bestD) continue;
    const own = (dumpHolds(d) ? crateTeam(state, d) : structureTeam(state, d)) === team;
    if (at <= near || own) { best = d; bestD = at; }
  }
  return best;
}
